import type { FC } from 'react';
import Btn from 'src/components-v2/Inputs/Button/Btn';
import Container from 'src/components-v2/Layout/Container/Container';
import Column from 'src/components-v2/Layout/Grid/Column';
import Row from 'src/components-v2/Layout/Grid/Row';
import { useComponentTheme } from 'src/hooks/useComponentTheme';
import Box from 'threads5/Box';
import styled from 'threads5/styles/styled';
import ThemeProvider from 'threads5/themes/theme-provider';
import ResponsiveImage from '../ResponsiveImage';

const StyledCallout = styled('section')(({ theme }) => {
  return {
    marginTop: '22px',
    position: 'relative',
    zIndex: 2, // Elevate above network section guilloché
    overflow: 'hidden',
    borderRadius: '16px',
    background: theme.palette['base/sky/900'],
    cursor: 'default',
    [theme.breakpoints.up('md')]: {
      marginTop: '60px',
    },
    [theme.breakpoints.up('lg')]: {
      marginTop: '180px',
      borderRadius: '24px',
    },
  };
});

const StyledBackgroundImage = styled(ResponsiveImage)(({ theme }) => {
  return {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'top center',
    [theme.breakpoints.up('md')]: {
      objectPosition: 'bottom right',
      transformOrigin: 'center right',
    },
  };
});

const StyledContent = styled(Box)(({ theme }) => {
  return {
    position: 'relative',
    zIndex: 1,
    color: theme.palette['base/white'],
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(2.125),
    padding: '35px 20px',
    height: '600px',
    textAlign: 'center',
    [theme.breakpoints.up('md')]: {
      justifyContent: 'center',
      alignItems: 'flex-start',
      textAlign: 'left',
      height: '377px',
      padding: '32px',
      gap: theme.spacing(4),
    },
    [theme.breakpoints.up('lg')]: {
      borderRadius: '24px',
      height: '707px',
      padding: '73px',
      gap: theme.spacing(6),
    },
  };
});

const StyledText = styled('h2')(({ theme }) => {
  return {
    margin: 0,
    fontFamily: theme.typography.plaidSans,
    color: theme.palette['base/white'],
    fontSize: '3.2rem',
    fontWeight: 500,
    lineHeight: '120%',
    letterSpacing: '-0.15rem',
    textWrap: 'balance',
    [theme.breakpoints.up('md')]: {
      maxWidth: '444px',
      lineHeight: '108%',
      letterSpacing: '-0.0898rem',
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: '743px',
      fontSize: '7rem',
      fontWeight: 500,
      lineHeight: '108%',
      letterSpacing: '-0.2rem',
    },
  };
});

const CtaCallout: FC = () => {
  const darkModeTheme = useComponentTheme('dark');

  return (
    <ThemeProvider theme={darkModeTheme}>
      <Container>
        <Row>
          <Column xs={22} xsOffset={1}>
            <StyledCallout>
              <StyledBackgroundImage
                className='background-image'
                srcs={{
                  xs: 'https://images.ctfassets.net/ss5kfr270og3/1qxMOb6SDxt6rrw2VFX8Gi/f2d14fee94306ac509317442fbe78973/homepage-ai-cta-background-mobile.png?fm=webp&q=70',
                  md: 'https://images.ctfassets.net/ss5kfr270og3/3MDVseoIEFjCCEdqYZrsBT/f0c1c3fed9659325fa03ff4632f931a8/homepage-ai-cta-background-tablet.png?fm=webp&q=70',
                  lg: 'https://images.ctfassets.net/ss5kfr270og3/PmJJzjbAOgxSN48lIsq4G/3a70a362cc4f0f5040be5970293966d8/homepage-ai-cta-background-desktop.png?fm=webp&q=70',
                }}
                alt=''
                loading='lazy'
              />

              <StyledContent>
                <StyledText>
                  The AI infrastructure behind smarter financial products
                </StyledText>

                <Btn
                  href='/intelligent-finance/'
                  variant='primary'
                  className='homepage-cta-callout-btn'
                >
                  Explore intelligent finance
                </Btn>
              </StyledContent>
            </StyledCallout>
          </Column>
        </Row>
      </Container>
    </ThemeProvider>
  );
};

export default CtaCallout;
